import { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import Modal from '@components/Modal';
import Button from '@components/DesignLibrary/Button';
import Image from 'next/image';
import PACERLogo from '@lib/Creators/Dashboard/Integrations/assets/pacer.png';
import DocumentRow from '@components/UserWorkflowInfo/DocumentsAction/DocumentRow';
import { Input } from '@/components/DesignLibrary/Atoms/Input';
import removeNullish from '@helpers/removeNullish';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import Spacer from '@components/Spacer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import TeamsContext from '@providers/Teams/TeamsContext';
import { DISTRICT_COURT_DATA, getDistrictIdByName, getDistrictNameById } from '@/helpers/districtCourtData';
import Select from '@/components/DesignLibrary/Atoms/Select/Select';
import { useFeatureFlag } from '@providers/FeatureFlags';
import s from './ECFManagerModal.module.scss';

type Props = {
  onClose: () => void;
  documents: Array<Parameters<typeof DocumentRow>[0]['compiledDocument']>;
  userWorkflowId: string;
  onSubmit?: () => Promise<void>;
};
const BANKRUPTCY_SCHEDULES_PROVIDER_LOCATER = 'noodle-bankruptcy-schedules-questionnaire-refactor';
const fieldTemplateKey = 'forge1b3f3bf097d211efb626932a321272a6';

const ECFManagerModal = ({ onClose, documents, userWorkflowId, onSubmit }: Props): ReactElement => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [district, setDistrict] = useState('');
  const [step, setStep] = useState(1);
  const [questionnaireId, setQuestionnaireId] = useState('');
  const hasFetchedForQuestionnaireId = useRef<string | null>(null);

  const {
    fetchingState: { isFetching: isSubmittingCase },
    getData: submitCase,
  } = useNoodleApi(tsClient.integrations.submitCase, { toastOnError: true });
  const { creatorId } = useContext(TeamsContext);
  const {
    getData: getQuestionnaireResponsesByTemplateKey,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireResponsesByTemplateKey);
  const {
    getData: getQuestionnaireIdByUserWorkflow,
  } = useNoodleApi(tsClient.workflows.getQuestionnaireIdByUserWorkflow);

  const useProdEnvironment = useFeatureFlag('use-pacer-prod-environment');

  useEffect(() => {
    const fetchResponses = async (): Promise<void> => {
      const response = await getQuestionnaireIdByUserWorkflow({
        questionnaireTemplateSlug: BANKRUPTCY_SCHEDULES_PROVIDER_LOCATER,
        userWorkflowId,
      });
      const newQuestionnaireId = response?.data?.questionnaireId ?? '';
      setQuestionnaireId(newQuestionnaireId);
      const { data } = await getQuestionnaireResponsesByTemplateKey({
        fieldTemplateKey,
        questionnaireId: newQuestionnaireId,
      });
      const courtKey = typeof data?.value === 'string' ? getDistrictIdByName(data.value) : null;
      if (courtKey) {
        setDistrict(courtKey);
      }
      hasFetchedForQuestionnaireId.current = newQuestionnaireId;
    };
    fetchResponses();
  }, [getQuestionnaireResponsesByTemplateKey, getQuestionnaireIdByUserWorkflow, userWorkflowId]);

  const handleDistrictSelection = (id: string): void => {
    setDistrict(id);
  };
  const onClick = async (): Promise<void> => {
    if (creatorId) {
      await submitCase({
        creatorId,
        district: getDistrictNameById(district),
        documentIds: documents.map((d) => d.document?.id).filter(removeNullish),
        password,
        provider: 'pacer',
        questionnaireId,
        useTestEnvironment: !useProdEnvironment,
        userWorkflowId,
        username,
      });
      if (onSubmit) {
        await onSubmit();
      }
      onClose();
    }
  };
  return (
    <Modal
      title={
        step === 1
          ? (
            <div className={s.modalTitle}>
              <Image src={PACERLogo} alt="Download" width={16} height={16} /> ECF Manager
            </div>
          )
          : ''
      }
      onClose={step > 1 ? () => setStep(step - 1) : onClose}
      closeIcon={step > 1 ? <ArrowCircleLeft size={24} weight="fill" /> : undefined}
      drag
    >
      <div className={s.container}>
        {step === 1 && (
          <>
            <div className={s.header}>
              <h2 className="heading-sm">Forms ready for filing</h2>
              <p className="caption">Select filing district and review before syncing the case data</p>
            </div>
            <Select
              label="Filing District"
              compact
              options={DISTRICT_COURT_DATA}
              value={district}
              optional={true}
              onChange={handleDistrictSelection}
            />
            <ul className={s.list}>
              {documents.map(doc => (
                <li key={doc.id}>
                  <DocumentRow compiledDocument={doc} fileNameOnly />
                </li>
              ))}
            </ul>
            <Button size="md" variant="primary" onClick={() => setStep(2)} disabled={!district}>
              Proceed
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <div className={s.header}>
              <Image src={PACERLogo} alt="Download" width={80} height={80} />
              <Spacer size={4} />
              <h2 className="heading-sm">Sync Data to PACER</h2>
            </div>
            <Select
              label="Filing District"
              compact
              options={DISTRICT_COURT_DATA}
              value={district}
              optional={true}
              onChange={handleDistrictSelection}
            />
            <Input
              label="Username"
              id="username"
              value={username}
              onChange={setUsername}
              compact
              optional
            />
            <Input
              label="Password"
              id="password"
              value={password}
              onChange={setPassword}
              type='password'
              compact
              optional
            />
            <Button
              size="md"
              variant="ai"
              onClick={onClick}
              disabled={!username || !password || !district}
              loading={isSubmittingCase}
            >
              Sync Case Data to PACER
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ECFManagerModal;
